/**
 * Project Metadata Sync Utilities
 * 
 * 🏗️ ARCHITECTURE: Injection Point Utilities
 * 
 * PURPOSE: Provides helper functions to inject project cache updates
 * into existing React Query mutation onSuccess hooks.
 * 
 * 🔒 DEVELOPER CONTEXT:
 * These utilities are called from existing mutation hooks to populate
 * our AI context cache. They are designed to be:
 * - Non-blocking (won't affect UI performance)
 * - Error-safe (won't break existing functionality)
 * - Transparent (invisible to end users)
 * 
 * INTEGRATION PATTERN:
 * 1. Import these utilities in existing mutation hooks
 * 2. Call appropriate sync function in onSuccess callback
 * 3. Cache updates happen asynchronously in background
 */

import { useUser } from '@/hooks/auth/use-user';
import { 
  updateProjectInCache, 
  removeProjectFromCache,
  updatePageInProjectCache,
  removePageFromProjectCache 
} from './project-navigation-cache';

/**
 * Extract goal information from goal-workbook junction data
 * Handles the junction table relationship to get goal details
 */
const extractGoalFromJunction = async (workbookId: string): Promise<{ id: string; title: string; } | undefined> => {
  try {
    // This would typically be done via the existing React Query data
    // but we'll provide a fallback lookup if needed
    
    // Import supabase dynamically to avoid circular dependencies
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase
      .from('planner_goals_workbooks')
      .select(`
        goal:goal_id (
          id,
          title
        )
      `)
      .eq('workbook_id', workbookId)
      .single();
    
    if (error || !data?.goal) return undefined;
    
    return {
      id: data.goal.id,
      title: data.goal.title
    };
  } catch (error) {
    console.warn('[ProjectSync] Could not extract goal info:', error);
    return undefined;
  }
};

/**
 * Extract agent thread ID from goal-workbook junction
 * Gets the chatroom ID for project navigation
 */
const extractChatroomId = async (workbookId: string): Promise<string | undefined> => {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase
      .from('planner_goals_workbooks')
      .select('agent_thread_id')
      .eq('workbook_id', workbookId)
      .single();
    
    if (error || !data?.agent_thread_id) return undefined;
    
    return data.agent_thread_id;
  } catch (error) {
    console.warn('[ProjectSync] Could not extract chatroom ID:', error);
    return undefined;
  }
};

/**
 * Safely extract user input from various project data sources
 * Handles both initial_request field and metadata.originalUserInput
 */
const extractUserInput = (projectData: any): string | undefined => {
  // First, try the direct initial_request field
  if (typeof projectData.initial_request === 'string' && projectData.initial_request.length > 0) {
    return projectData.initial_request;
  }
  
  // Fallback to metadata.originalUserInput (common in AI-generated projects)
  if (projectData.metadata?.originalUserInput && typeof projectData.metadata.originalUserInput === 'string') {
    return projectData.metadata.originalUserInput;
  }
  
  // No user input found
  return undefined;
};

/**
 * Sync project creation to cache
 * Call this from useWorkbookOperations.createWorkbook.onSuccess
 */
export const syncProjectCreated = async (
  projectData: {
    id: string;
    title: string;
    type?: string;
    initial_request?: string;
    metadata?: any; // Add metadata support for extracting user input
  },
  userId: string
): Promise<void> => {
  try {
    console.log('[ProjectSync] Syncing created project:', projectData.title);
    
    // Extract goal and chatroom info asynchronously
    const [goal, chatroomId] = await Promise.all([
      extractGoalFromJunction(projectData.id),
      extractChatroomId(projectData.id)
    ]);
    
    // Safely extract user input from available sources
    const userInput = extractUserInput(projectData);
    
    // Prepare cache data
    const cacheData = {
      id: projectData.id,
      title: projectData.title,
      type: projectData.type,
      overview: userInput ? userInput.substring(0, 200) : undefined, // Safe truncation
      chatroomId,
      goal
    };
    
    // Update cache (non-blocking)
    updateProjectInCache(cacheData, userId);
    
    console.log('[ProjectSync] Project sync completed:', {
      projectId: projectData.id,
      hasGoal: !!goal,
      hasChatroom: !!chatroomId,
      hasUserInput: !!userInput
    });
    
  } catch (error) {
    // Non-critical error - don't break the flow
    console.error('[ProjectSync] Error syncing project creation:', error);
  }
};

/**
 * Sync project deletion from cache
 * Call this from useWorkbookOperations.deleteWorkbook.onSuccess
 */
export const syncProjectDeleted = (projectId: string, userId: string): void => {
  try {
    console.log('[ProjectSync] Syncing deleted project:', projectId);
    
    // Remove from cache (non-blocking)
    removeProjectFromCache(projectId, userId);
    
  } catch (error) {
    // Non-critical error - don't break the flow
    console.error('[ProjectSync] Error syncing project deletion:', error);
  }
};

/**
 * Sync project updates to cache
 * Call this from usePageMutations.updatePage.onSuccess for title changes
 */
export const syncProjectUpdated = async (
  projectData: {
    id: string;
    title?: string;
    type?: string;
    initial_request?: string;
    metadata?: any; // Add metadata support
  },
  userId: string
): Promise<void> => {
  try {
    console.log('[ProjectSync] Syncing updated project:', projectData.id);
    
    // Safely extract user input
    const userInput = extractUserInput(projectData);
    
    // Only update if we have meaningful changes
    if (!projectData.title && !projectData.type && !userInput) {
      return;
    }
    
    // Get existing goal and chatroom info if not provided
    const [goal, chatroomId] = await Promise.all([
      extractGoalFromJunction(projectData.id),
      extractChatroomId(projectData.id)
    ]);
    
    // Prepare cache data with only changed fields
    const cacheData: any = {
      id: projectData.id
    };
    
    if (projectData.title) cacheData.title = projectData.title;
    if (projectData.type) cacheData.type = projectData.type;
    if (userInput) {
      cacheData.overview = userInput.substring(0, 200); // Safe truncation
    }
    if (chatroomId) cacheData.chatroomId = chatroomId;
    if (goal) cacheData.goal = goal;
    
    // Update cache (non-blocking)
    updateProjectInCache(cacheData, userId);
    
  } catch (error) {
    // Non-critical error - don't break the flow
    console.error('[ProjectSync] Error syncing project update:', error);
  }
};

/**
 * Sync page creation to cache
 * Call this from usePageContentMutations.createPage.onSuccess
 */
export const syncPageCreated = (
  projectId: string,
  pageData: {
    id: string;
    title: string;
    type: string;
    content?: string;
    writing_style?: string;
  },
  userId: string
): void => {
  try {
    console.log('[ProjectSync] Syncing created page:', pageData.title);
    
    // Prepare cache data
    const cacheData = {
      id: pageData.id,
      title: pageData.title,
      type: pageData.type,
      hasContent: pageData.content ? pageData.content.length > 50 : false // Basic content check
    };
    
    // Update cache (non-blocking)
    updatePageInProjectCache(projectId, cacheData, userId);
    
  } catch (error) {
    // Non-critical error - don't break the flow
    console.error('[ProjectSync] Error syncing page creation:', error);
  }
};

/**
 * Sync page deletion from cache
 * Call this from usePageContentMutations.deletePage.onSuccess
 */
export const syncPageDeleted = (
  projectId: string,
  pageId: string,
  userId: string
): void => {
  try {
    console.log('[ProjectSync] Syncing deleted page:', pageId);
    
    // Remove from cache (non-blocking)
    removePageFromProjectCache(projectId, pageId, userId);
    
  } catch (error) {
    // Non-critical error - don't break the flow
    console.error('[ProjectSync] Error syncing page deletion:', error);
  }
};

/**
 * Sync page updates to cache
 * Call this from usePageContentMutations.updatePage.onSuccess for content/title changes
 */
export const syncPageUpdated = (
  projectId: string,
  pageData: {
    id: string;
    title?: string;
    type?: string;
    content?: string;
    writing_style?: string;
  },
  userId: string
): void => {
  try {
    console.log('[ProjectSync] Syncing updated page:', pageData.id);
    
    // Only update if we have meaningful changes
    if (!pageData.title && !pageData.type && pageData.content === undefined && !pageData.writing_style) {
      return;
    }
    
    // Prepare cache data with only changed fields
    const cacheData: any = {
      id: pageData.id
    };
    
    if (pageData.title) cacheData.title = pageData.title;
    if (pageData.type) cacheData.type = pageData.type;
    if (pageData.content !== undefined) {
      cacheData.hasContent = pageData.content.length > 50; // Basic content check
    }
    
    // Update cache (non-blocking)
    updatePageInProjectCache(projectId, cacheData, userId);
    
  } catch (error) {
    // Non-critical error - don't break the flow
    console.error('[ProjectSync] Error syncing page update:', error);
  }
};

/**
 * Hook-based wrapper for easier integration
 * Use this in components that need to sync cache updates
 */
export const useProjectCacheSync = () => {
  const { user } = useUser();
  
  return {
    syncProjectCreated: (projectData: Parameters<typeof syncProjectCreated>[0]) => 
      user?.id ? syncProjectCreated(projectData, user.id) : Promise.resolve(),
      
    syncProjectDeleted: (projectId: string) => 
      user?.id ? syncProjectDeleted(projectId, user.id) : undefined,
      
    syncProjectUpdated: (projectData: Parameters<typeof syncProjectUpdated>[0]) => 
      user?.id ? syncProjectUpdated(projectData, user.id) : Promise.resolve(),
      
    syncPageCreated: (projectId: string, pageData: Parameters<typeof syncPageCreated>[1]) => 
      user?.id ? syncPageCreated(projectId, pageData, user.id) : undefined,
      
    syncPageDeleted: (projectId: string, pageId: string) => 
      user?.id ? syncPageDeleted(projectId, pageId, user.id) : undefined,
      
    syncPageUpdated: (projectId: string, pageData: Parameters<typeof syncPageUpdated>[1]) => 
      user?.id ? syncPageUpdated(projectId, pageData, user.id) : undefined,
  };
};

/**
 * Utility to safely extract project ID from various data structures
 * Handles different mutation response formats
 */
export const extractProjectId = (data: any): string | null => {
  // Try different common property names
  const possibleIds = [
    data?.id,
    data?.workbook_id,
    data?.workbookId,
    data?.projectId,
    data?.project_id
  ];
  
  for (const id of possibleIds) {
    if (typeof id === 'string' && id.length > 0) {
      return id;
    }
  }
  
  console.warn('[ProjectSync] Could not extract project ID from:', data);
  return null;
};

/**
 * Utility to safely extract page ID from mutation response
 * Handles different mutation response formats
 */
export const extractPageId = (data: any): string | null => {
  const id = data?.id;
  
  if (typeof id === 'string' && id.length > 0) {
    return id;
  }
  
  console.warn('[ProjectSync] Could not extract page ID from:', data);
  return null;
}; 